import DetailsTitle from "components/common/detailsPage/DetailsTitle";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { ApiLogBadge } from "components/common/StatusBadge";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { formatUTCDate } from "utils/utils";
import InfoItem from "components/common/detailsPage/InfoItem";
import { getAPILogDetails } from "store/slices/apiLogsSlice";
import { Copy, Download } from "lucide-react";
import { showAlert } from "store/slices/alertSlice";
import HorizontalScroller from "components/common/HorizontalScroller";
import { getMembershipLogDetails } from "store/slices/membershipLogsSlice";
import { MEMBERSHIP_EVENTS_OPTIONS } from "utils/constants";
import TruncatedText from "components/common/TruncatedText";

const MembershipLogDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const dispatch = useDispatch();
  const id = useQueryParam("id");

  const { canAction } = usePermission();
  const { canView } = canAction("api_logs");

  const { loading, itemDetails } = useSelector((state) => state.MembershipLogs);

  const {
    subscriptionInfo,
    customer,
    performedBy,
    membershipInfo,
    metadata,
    detailedStateChange,
  } = itemDetails || {};

  useEffect(() => {
    if (id && canView) {
      dispatch(getMembershipLogDetails(id));
    }
  }, [id, canView]);

  const formattedData = (data) => data?.replace(/\n/g, "<br>");

  const copyToClipboard = (data, type) => {
    const dataString = JSON.stringify(data, null, 2);

    const textarea = document.createElement("textarea");
    textarea.value = dataString;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "absolute";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    if (dataString === "null") {
      return dispatch(showAlert({ text: t("Nothing to copy"), type: "error" }));
    }

    try {
      textarea.select();
      const successful = document.execCommand("copy");
      if (successful) {
        dispatch(
          showAlert({
            text: t(`${type} copied to clipboard!`),
            type: "success",
          }),
        );
      } else {
        dispatch(showAlert({ text: t("Failed to copy"), type: "error" }));
      }
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "error" }));
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const downloadJSON = (data) => {
    const dataString = JSON.stringify(data, null, 2);
    if (dataString === "null") {
      return dispatch(
        showAlert({ text: t("Nothing to download"), type: "error" }),
      );
    }
    const blob = new Blob([dataString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "data.json";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const membershipEventMap = Object.fromEntries(
    MEMBERSHIP_EVENTS_OPTIONS.map((item) => [item.value, item.name]),
  );

  const subscriptionInfoFields = [
    // ───── Customer Info ─────
    {
      label: t("Log ID"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{subscriptionInfo?.logId || "-"}</div>
        </HorizontalScroller>
      ),
      col: { lg: 6 },
    },
    {
      label: t("Module"),
      value: subscriptionInfo?.module || "-",
      col: { lg: 6 },
    },
    {
      label: t("Action"),
      value: (
        <span
          className={`action-tag ${subscriptionInfo?.action?.toLowerCase()}`}
        >
          {membershipEventMap[subscriptionInfo?.action] ||
            subscriptionInfo?.action ||
            "-"}
        </span>
      ),
      col: { lg: 6 },
    },
    {
      label: t("Date & Time: "),
      value: formatUTCDate(subscriptionInfo?.dateTime),
      col: { lg: 6 },
    },
    {
      label: t("Summery"),
      value: <TruncatedText text={subscriptionInfo?.summary || ""} />,
      col: { lg: 12 },
    },
  ];

  const customerInfoFields = [
    {
      label: t("Id"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{customer?._id || "-"}</div>
        </HorizontalScroller>
      ),

      col: { lg: 6 },
    },
    {
      label: t("Name"),
      value: customer?.name || "-",
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Phone"),
      value: customer?.phone || "-",
      col: { lg: 6 },
    },
  ];
  const performedByFields = [
    {
      label: t("Id"),
      render: (
        <HorizontalScroller t={t}>
          <div className="value">{performedBy?._id || "-"}</div>
        </HorizontalScroller>
      ),
      col: { lg: 6 },
    },
    {
      label: t("Name"),
      value: performedBy?.name || "-",
      col: { lg: 6 },
    },
    { isSpacer: true },
    {
      label: t("Role"),
      value: performedBy?.role || "-",
      col: { lg: 6 },
    },
    {
      label: t("Model"),
      value: performedBy?.model || "-",
      col: { lg: 6 },
    },
    { isSpacer: true },

    {
      label: t("Impersonate"),
      value: performedBy?.impersonate || "-",
      col: { lg: 6 },
    },
  ];
  const membershipInfoFields = [
    {
      label: t("Id"),
      value: membershipInfo?._id || "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Membership No"),
      value: membershipInfo?.membershipNo || "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Membership Type"),
      value: membershipInfo?.membershipType || "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Plan Type"),
      value: membershipInfo?.planType || "-",
      col: { lg: 3, md: 6 },
    },
    { isSpacer: true },
    {
      label: t("License Plate Number"),
      value: membershipInfo?.licencePlateNumber || "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Status"),
      value: membershipInfo?.status === 1 ? t("Active") : t("Inactive"),
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Billing Cycle"),
      value: membershipInfo?.selectedPricing?.label || "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Billing Cycle Start"),
      value: formatUTCDate(membershipInfo?.billingCycleStart),
      col: { lg: 3, md: 6 },
    },
    { isSpacer: true },
    {
      label: t("Billing Cycle End"),
      value: formatUTCDate(membershipInfo?.billingCycleEnd),
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Auto Renew"),
      value: membershipInfo?.autoRenew ? t("Yes") : t("No"),
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Final Price"),
      value: membershipInfo?.finalPrice
        ? `${membershipInfo.finalPrice} ${membershipInfo?.currency || ""}`
        : "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Base Price"),
      value: membershipInfo?.selectedPricing?.basePrice
        ? `${membershipInfo.selectedPricing.basePrice} ${membershipInfo?.currency || ""}`
        : "-",
      col: { lg: 3, md: 6 },
    },
    { isSpacer: true },

    {
      label: t("Duration in days"),
      value: membershipInfo?.selectedPricing?.durationInDays
        ? `${membershipInfo.selectedPricing.durationInDays}`
        : "-",
      col: { lg: 3, md: 6 },
    },
    {
      label: t("Calculated at"),
      value: formatUTCDate(membershipInfo?.selectedPricing?.calculatedAt),
      col: { lg: 3, md: 6 },
    },
  ];

  const shimmerLayout = [
    {
      col: 4,
      blocks: [
        { rows: 2, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 4,
      blocks: [
        { rows: 1, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 4,
      blocks: [
        { rows: 2, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 12,
      blocks: [
        { rows: 3, cols: 4 },
        { rows: 1, cols: 2 },
      ],
    },
    {
      col: 12,
      type: "details_page_tab",
      blocks: [{ rows: 1, cols: 2 }],
    },
  ];

  if (!canView) return null;

  if (loading || !itemDetails) {
    // if (!loading) {
    return (
      <div className="page-content">
        <DetailsTitle title={t("Membership Log Details")} />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page membership_log_details">
      <DetailsTitle title={t("Membership Log Details")} />

      <Row>
        <Col lg={4} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="general_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Subscription Information")}</h4>
              </Col>

              {subscriptionInfoFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        <Col lg={4} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="device_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Customer Information")}</h4>
              </Col>
              {customerInfoFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                    classname="customer_info"
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        <Col lg={4} className="">
          <div className="common_card" style={{ height: "calc(100%  - 20px)" }}>
            <Row id="device_detail_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Performed By")}</h4>
              </Col>
              {performedByFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        <Col lg={12} className="">
          <div
            className="common_card membership_info_card"
            style={{ height: "calc(100% - 20px)" }}
          >
            <Row id="membership_info_row">
              <Col lg={12} className="mb-3 details_heading">
                <h4>{t("Membership Information")}</h4>
              </Col>
              {membershipInfoFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100 spacer" />;
                }
                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                    classname="membership_info_col"
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <div className="details_page_title">
            <h2 className="mt-4">{t("Detailed State Change")}</h2>
          </div>
          <Row>
            <Col lg={6}>
              <div className="state_changes_wrapper common_card">
                <span className="state_change_title previous">
                  {t("Previous State")}
                </span>

                <div className="bg-black text-white p-3 rounded-4 position-relative state_change_viewer">
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: formattedData(
                        JSON.stringify(
                          detailedStateChange?.previousState,
                          null,
                          2,
                        ),
                      ),
                    }}
                  />
                  <Copy
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 55,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      copyToClipboard(
                        detailedStateChange?.previousState,
                        "Previous state",
                      )
                    }
                  />
                  <Download
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      downloadJSON(detailedStateChange?.previousState)
                    }
                  />
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="state_changes_wrapper common_card">
                <span className="state_change_title new">{t("New State")}</span>

                <div className="bg-black text-white p-3 rounded-4 position-relative state_change_viewer">
                  <pre
                    dangerouslySetInnerHTML={{
                      __html: formattedData(
                        JSON.stringify(detailedStateChange?.newState, null, 2),
                      ),
                    }}
                  />
                  <Copy
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 55,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      copyToClipboard(
                        detailedStateChange?.newState,
                        "New state",
                      )
                    }
                  />
                  <Download
                    style={{
                      position: "absolute",
                      top: 20,
                      right: 20,
                      cursor: "pointer",
                    }}
                    onClick={() => downloadJSON(detailedStateChange?.newState)}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default MembershipLogDetails;
